import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Activity, ArrowRight, RefreshCw, LogIn, ShieldAlert, Trash2, RotateCcw,
  KeyRound, Power, ShieldOff, Edit, Plus, AlertTriangle, FileX, Database, Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact action metadata: { label, color, Icon }
const ACTION_META = {
  soft_delete: { label: 'Soft Delete', color: 'text-orange-400', icon: Trash2 },
  permanent_delete: { label: 'Hapus Permanen', color: 'text-red-400', icon: AlertTriangle },
  restore: { label: 'Restore', color: 'text-emerald-400', icon: RotateCcw },
  auto_purge: { label: 'Auto Purge', color: 'text-rose-400', icon: Trash2 },
  create: { label: 'Create', color: 'text-blue-400', icon: Plus },
  update: { label: 'Update', color: 'text-indigo-400', icon: Edit },
  login_success: { label: 'Login Berhasil', color: 'text-emerald-400', icon: LogIn },
  login_failed: { label: 'Login Gagal', color: 'text-red-400', icon: ShieldAlert },
  change_password: { label: 'Ganti Password', color: 'text-violet-400', icon: KeyRound },
  customer_change_password: { label: 'Ganti Password (Customer)', color: 'text-violet-400', icon: KeyRound },
  reset_password: { label: 'Reset Password', color: 'text-orange-400', icon: KeyRound },
  suspend_tenant: { label: 'Suspend Tenant', color: 'text-red-400', icon: Power },
  activate_tenant: { label: 'Activate Tenant', color: 'text-emerald-400', icon: Power },
  bulk_delete_tenants: { label: 'Bulk Delete Tenants', color: 'text-red-400', icon: AlertTriangle },
  reset_database: { label: 'Reset Database', color: 'text-red-400', icon: Database },
  reset_all_invoices: { label: 'Reset Semua Tagihan', color: 'text-red-400', icon: FileX },
  reset_all_payments: { label: 'Reset Semua Pembayaran', color: 'text-red-400', icon: FileX },
  delete_all_invoices: { label: 'Delete All Invoices', color: 'text-red-400', icon: FileX },
  manual_isolir_run: { label: 'Manual Isolir', color: 'text-amber-400', icon: ShieldOff },
  manual_unisolir: { label: 'Manual Unisolir', color: 'text-emerald-400', icon: Activity },
  delete_old_logs: { label: 'Hapus Log Lama', color: 'text-slate-400', icon: Trash2 },
  cascade_soft_delete_backfill: { label: 'Cascade Backfill', color: 'text-amber-400', icon: Trash2 },
};

const fallbackMeta = (action) => ({
  label: action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  color: 'text-slate-400',
  icon: Activity,
});

// Indonesian relative time formatter
const timeAgo = (iso) => {
  if (!iso) return '';
  const now = new Date();
  const t = new Date(iso);
  let s = Math.floor((now - t) / 1000);
  if (Number.isNaN(s) || s < 0) return '';
  if (s < 60) return `${s} detik lalu`;
  let m = Math.floor(s / 60);
  if (m < 60) return `${m} menit lalu`;
  let h = Math.floor(m / 60);
  if (h < 24) return `${h} jam lalu`;
  let d = Math.floor(h / 24);
  if (d < 30) return `${d} hari lalu`;
  let mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} bulan lalu`;
  return `${Math.floor(mo / 12)} tahun lalu`;
};

const buildSummary = (log) => {
  // changes can be JSON string or object
  let changes = log.changes;
  if (typeof changes === 'string') {
    try { changes = JSON.parse(changes); } catch { changes = {}; }
  }
  changes = changes || {};

  const meta = ACTION_META[log.action] || fallbackMeta(log.action);
  const target = log.entity_name || log.entity_id || '';
  const entity = log.entity_type || '';

  // Build a short readable description
  let detail = '';
  if (log.action === 'login_success') {
    detail = `${changes.role ? `[${changes.role}] ` : ''}berhasil login`;
  } else if (log.action === 'login_failed') {
    detail = `gagal login: ${changes.reason || 'unknown'}`;
  } else if (log.action === 'change_password') {
    detail = changes.self ? 'mengganti password sendiri' : 'mengganti password';
  } else if (log.action === 'reset_password') {
    detail = `reset password ${entity}${target ? ' ' + target : ''}`;
  } else if (log.action === 'soft_delete') {
    detail = `soft delete ${entity}${target ? ' "' + target + '"' : ''}`;
    if (changes.cascaded_customers) detail += ` (+${changes.cascaded_customers} customer)`;
  } else if (log.action === 'restore') {
    detail = `restore ${entity}${target ? ' "' + target + '"' : ''}`;
  } else if (log.action === 'permanent_delete') {
    detail = `hapus permanen ${entity}${target ? ' "' + target + '"' : ''}`;
  } else if (log.action === 'manual_isolir_run' || log.action === 'manual_unisolir') {
    detail = meta.label;
  } else {
    detail = `${meta.label}${entity ? ' ' + entity : ''}${target ? ' "' + target + '"' : ''}`;
  }
  return { meta, detail };
};

// Variant-specific styling so the card blends into glass/light/dark dashboards
const VARIANT_STYLES = {
  dark: {
    container: 'rounded-xl border bg-slate-900/60 backdrop-blur p-4 sm:p-5 relative overflow-hidden',
    containerStyle: { borderColor: 'rgba(96, 165, 250, 0.25)' },
    title: 'text-sm font-semibold text-slate-200 tracking-wide',
    iconBoxBg: 'rgba(96, 165, 250, 0.15)',
    iconBoxColor: '#60a5fa',
    refreshBtn: 'text-xs text-slate-400 hover:text-blue-400 transition-colors flex items-center gap-1 disabled:opacity-50',
    viewAll: 'text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors',
    item: 'flex items-start gap-3 p-2.5 rounded-lg hover:bg-white/5 transition-colors border border-transparent hover:border-slate-700/50',
    userText: 'text-sm text-slate-200 truncate',
    userBold: 'font-semibold text-slate-100',
    ipText: 'text-xs text-slate-500 ml-1.5 font-mono',
    detailText: 'text-xs text-slate-400 truncate mt-0.5',
    timeText: 'flex items-center gap-1 text-xs text-slate-600 mt-1',
    emptyText: 'text-center py-8 text-slate-500 text-sm',
  },
  light: {
    container: 'rounded-xl border border-slate-200 bg-white shadow-sm p-4 sm:p-5 relative overflow-hidden',
    containerStyle: {},
    title: 'text-sm font-semibold text-slate-700 tracking-wide',
    iconBoxBg: 'rgba(59, 130, 246, 0.12)',
    iconBoxColor: '#3b82f6',
    refreshBtn: 'text-xs text-slate-500 hover:text-blue-600 transition-colors flex items-center gap-1 disabled:opacity-50',
    viewAll: 'text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors',
    item: 'flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200',
    userText: 'text-sm text-slate-700 truncate',
    userBold: 'font-semibold text-slate-900',
    ipText: 'text-xs text-slate-500 ml-1.5 font-mono',
    detailText: 'text-xs text-slate-500 truncate mt-0.5',
    timeText: 'flex items-center gap-1 text-xs text-slate-400 mt-1',
    emptyText: 'text-center py-8 text-slate-400 text-sm',
  },
  glass: {
    container: 'rounded-2xl border bg-white/70 backdrop-blur-xl shadow-lg p-4 sm:p-5 relative overflow-hidden',
    containerStyle: { borderColor: 'rgba(139, 92, 246, 0.25)' },
    title: 'text-sm font-semibold text-slate-800 tracking-wide',
    iconBoxBg: 'rgba(139, 92, 246, 0.15)',
    iconBoxColor: '#8b5cf6',
    refreshBtn: 'text-xs text-slate-500 hover:text-violet-600 transition-colors flex items-center gap-1 disabled:opacity-50',
    viewAll: 'text-xs text-violet-600 hover:text-violet-700 flex items-center gap-1 transition-colors',
    item: 'flex items-start gap-3 p-2.5 rounded-lg hover:bg-violet-50/50 transition-colors border border-transparent hover:border-violet-200/50',
    userText: 'text-sm text-slate-700 truncate',
    userBold: 'font-semibold text-slate-900',
    ipText: 'text-xs text-slate-500 ml-1.5 font-mono',
    detailText: 'text-xs text-slate-500 truncate mt-0.5',
    timeText: 'flex items-center gap-1 text-xs text-slate-400 mt-1',
    emptyText: 'text-center py-8 text-slate-400 text-sm',
  },
};

// Map dark Tailwind colors to lighter variants for light/glass
const remapColor = (color, variant) => {
  if (variant === 'dark') return color;
  // change "text-X-400" to "text-X-600"
  return color.replace(/-400$/, '-600').replace(/-500$/, '-600');
};

const RecentActivityCard = ({ variant = 'dark' }) => {
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.dark;
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/v2/audit-logs?limit=15`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res?.data?.data || [];
      setLogs(data);
    } catch (e) {
      // silent fail — keep prior state
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(id);
  }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  return (
    <div
      className={styles.container}
      style={styles.containerStyle}
      data-testid="recent-activity-card"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-400/40 to-transparent" />
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: styles.iconBoxBg, color: styles.iconBoxColor }}>
            <Activity className="w-4 h-4" />
          </div>
          <h3 className={styles.title}>Aktivitas Terbaru</h3>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={styles.refreshBtn}
            data-testid="recent-activity-refresh"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <Link
            to="/app/audit-logs"
            className={styles.viewAll}
            data-testid="recent-activity-view-all"
          >
            Lihat Semua <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto pr-1 custom-scrollbar">
        {loading ? (
          <div className={styles.emptyText}>Memuat aktivitas...</div>
        ) : logs.length === 0 ? (
          <div className={styles.emptyText}>Belum ada aktivitas</div>
        ) : (
          logs.map((log) => {
            const { meta, detail } = buildSummary(log);
            const Icon = meta.icon;
            const iconColor = remapColor(meta.color, variant);
            return (
              <div
                key={log.id}
                className={styles.item}
                data-testid={`activity-item-${log.id}`}
              >
                <div className={`mt-0.5 ${iconColor}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={styles.userText}>
                    <span className={styles.userBold}>
                      {log.username || 'system'}
                    </span>
                    {log.ip_address && (
                      <span className={styles.ipText}>({log.ip_address})</span>
                    )}
                  </div>
                  <div className={styles.detailText}>{detail}</div>
                  <div className={styles.timeText}>
                    <Clock className="w-3 h-3" />
                    <span>{timeAgo(log.created_at)}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RecentActivityCard;
